/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
// styles
import { ProgramPanel, SchoolPanel } from '@jo-v1/features/JoRecommender/ProgramSelect/SchoolProgramTabs';
import { useEffect, useState, useMemo, memo, useRef } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useProductEvents from '@core/hooks/cohesion/useProductEvents';
import useIntersectionObserver from '@core/hooks/useIntersectionObserver';
// selectors, actions
import {
  selectHeclidMap,
  selectRecommenderPopupListId,
  selectProductCorrelationIdMap,
  setProductCorrelationIdMapEntry,
  setViewCorrelationIdMapEntry,
} from '@core/reducers/eventingSlice';
import { setLeadDelivery, selectSubmissions, addSubmission } from '@core/reducers/matchesSlice';
import { mobiusGetApplicationCta } from '@core/services/mobius';
import { VoyagerResult } from '@core/ts/results';
// hooks & utils
import cn from '@core/utils/classNames';

import ComparisonCheckbox from '../CompareCheckbox';
import styles from './JoRelatedCard.module.css';

type JoRelatedCardProps = {
  result: VoyagerResult;
  position?: number;
  eventingOverrides?: any;
  index?: number;
  fullWidth?: boolean;
};

const JoRelatedCard = ({ result, position, eventingOverrides, index, fullWidth = false }: JoRelatedCardProps) => {
  // dispatches actions
  const dispatch = useDispatch();

  // local state
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [applicationCtaData, setApplicationCtaData] = useState<{ label: string; url: string } | null>(null);

  // Store
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  // Eventing Ids
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);

  const cardRef = useRef<HTMLElement | null>(null);
  const isInView = useIntersectionObserver(cardRef);

  // Initialize events
  const { productLoaded, productViewed, productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location: 'voyager-related-results',
      formatType: 'app',
      formatSubtype: 'jo-stacked',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });

  useEffect(() => {
    if (isInView && !isProgramSubmitted) {
      productViewed();
    }
  }, [isInView]);

  // Fire productLoaded + productViewed events and adds entry to the viewCorrelationIdMap
  useEffect(() => {
    productLoaded();
    if (result?.program?.id) {
      dispatch(
        setViewCorrelationIdMapEntry({
          key: result.program.id,
          value: newViewCorrelationId,
        })
      );
    }
  }, []);

  useEffect(() => {
    const getApplicationCtaData = async () => {
      const response = await mobiusGetApplicationCta({
        programId: `${result.program.id}`,
        heclid: heclidMap[result.program.id],
        correlationId: productCorrelationIdMap[result.program.id],
      });
      setApplicationCtaData({ label: response?.label, url: response?.url });
    };

    getApplicationCtaData();
  }, []);

  // Matches State properties
  const submissions = useSelector(selectSubmissions);
  const isProgramSubmitted = submissions.some((s) => s.program.id === result.program.id);
  const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result.school.id);
  const handleProductClick = () => {
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: true,
          currentPrograms: [result],
          showExitStep: true,
        })
      );
      dispatch(addSubmission({ ...result, duplicate: true }));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    const productCorrelationId = uuid();
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));
    productClicked({ correlationId: productCorrelationId });

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };

  const flags = useFeatureFlags();
  const isComparisonTest = !!flags.programsComparisonTest;

  return (
    <section ref={cardRef} className={cn(styles.card, fullWidth ? styles.fullWidthCard : undefined)}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsDescriptionExpanded((prev) => !prev);
          }
        }}
        className={styles.bodyContainer}
      >
        <div className={styles.cardHeader}>
          <div className={styles.schoolNameContainer}>
            <div className={styles.logo}>
              <img
                src={
                  result?.school?.slug
                    ? `https://simple-storage-server.highereducation.com/${result.school.slug}.png?w=130&h=80`
                    : undefined
                }
                alt={result?.school?.name}
              />
            </div>
            <div className={styles.schoolNameLocation}>
              <div className={styles.schoolNameLocationWrapper}>
                <h4 className={styles.title}>{result?.school?.name}</h4>
                {result?.school?.city && result?.school?.state && (
                  <div className={styles.location}>
                    {result.school.city}, {result.school.state}
                  </div>
                )}
              </div>
              <h3 className={styles.programNameDesktop}>{result?.program?.name}</h3>
            </div>
          </div>
          <h3 className={styles.programNameMobile}>{result?.program?.name}</h3>
        </div>
        <div className={styles.buttonsContainer}>
          {!isProgramSubmitted ? (
            <button
              onClick={handleProductClick}
              data-testid={`recommender-popup-card-button-${position}`}
              type="button"
              className={styles.cardBtnCta}
            >
              Explore Program
            </button>
          ) : (
            <button
              onClick={
                applicationCtaData?.url ? () => window.open(applicationCtaData?.url, '_blank') : handleProductClick
              }
              data-testid={`recommender-popup-card-button-${position}`}
              type="button"
              className={styles.cardBtnCtaSubmitted}
            >
              {applicationCtaData?.label || 'Next Steps'}
            </button>
          )}
          <span className={styles.expandDescriptionButton}>
            About This Program
            <MdKeyboardArrowDown size={18} className={isDescriptionExpanded ? styles.arrowUp : undefined} />
          </span>
          {isComparisonTest && (
            <span className={styles.checkboxWrapper}>
              <ComparisonCheckbox result={result} />
            </span>
          )}
        </div>
      </div>
      <div className={cn(styles.descriptionContent, isDescriptionExpanded && styles.expanded)}>
        {result?.program?.snippet && (
          <>
            <div className={styles.programPanelWrapper}>
              <h4 className={styles.programPanelTitle}>Program Info</h4>
              <ProgramPanel programSnippet={result?.program?.snippet} />
            </div>
          </>
        )}
        {result?.school?.additional && (
          <>
            <div className={styles.schoolPanelWrapper}>
              <h4>School Info</h4>
              <SchoolPanel
                schoolAdditionalInfo={{
                  ...result.school.additional,
                  accreditator:
                    result.school.additional.accreditator === null ? undefined : result.school.additional.accreditator,
                }}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

// We use memo to prevent rerenders when props and state haven't changed
export default memo(JoRelatedCard);
